/* Liebreiz GA4 views v1.2: event-only companion, never a base tag or Shopify pixel.
 * Local candidate, 2026-10-04. No config/js/consent calls, no conversion sender.
 * Install BEFORE deferred LWS core. Existing events are never replayed; only
 * exact native page_view command metadata may passively establish enqueue order.
 */
(function (scope, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (scope && scope.document && !scope.LWSGa4ViewsV1) {
    scope.LWSGa4ViewsV1 = api.bootstrap(scope);
  }
})(typeof window === 'object' ? window : null, function () {
  'use strict';
  const MEASUREMENT_ID = 'G-Q96X0YP2JW';
  // Matches the existing Google & YouTube app's freshly inspected configuration.
  const COUNTRY = 'ZZ';
  const LANDINGS = new Set(['preisbrecher', 'classic-lab', 'exklusiv-natur', 'classic-six', 'sortiment']);
  const HOSTS = new Set(['www.liebreiz-shop.de', 'liebreiz-shop.de']);
  const CDN_PATH = '/s/files/1/0957/7581/8115/files/';
  const MEMOIRE_INDEX = 'https://cdn.shopify.com' + CDN_PATH + 'lws-memoire-native-index-v1.json?v=1790714171';
  const SKU = /^[A-Za-z0-9][A-Za-z0-9-]{2,95}$/;
  const HANDLE = /^lws-[a-z0-9][a-z0-9-]{0,170}$/;
  const ID = /^[1-9]\d{0,15}$/;
  const MAX_LIST = 20;
  const PRICE_TTL_MS = 10000;
  const INDEX_TTL_MS = 300000;
  const VIEW_DEADLINE_MS = 10000;
  const INSTALL_KEY = '__lwsGa4ViewsV1';
  const MAX_NATIVE_METADATA_SCAN = 5000;

  function isNativePageView(entry) {
    try {
      // App contract verified 2026-10-04, public pixel SHA256 88f7c196...df6e9c.
      // Metadata ONLY. Never enumerate/copy/read event_id/ext_client_id/user_data,
      // CID/SID/UTMs or any other native payload parameter.
      if (!Array.isArray(entry) && Object.prototype.toString.call(entry) !== '[object Arguments]') return false;
      if (entry[0] !== 'event' || entry[1] !== 'page_view') return false;
      const parameters = entry[2];
      return !!parameters && typeof parameters === 'object' && parameters.send_to === MEASUREMENT_ID && parameters.shopify_event_name === 'page_viewed';
    } catch (_) { return false; }
  }

  function initialListGuard(scope, now) {
    // This sentinel starts at ASSET/bootstrap entry, including queue/SDK waits.
    // No assertion that consent existed at document creation, and no cookie read.
    const started = now(), initial = new URL(scope.location.href);
    const lp = LANDINGS.has(initial.searchParams.get('lp')) ? initial.searchParams.get('lp') : 'classic-six';
    let blocked = null;
    const state = { attempted: false, status: 'waiting-current-dom', landing: lp };
    function block(reason) { if (!blocked) { blocked = reason; state.status = reason; } }
    function probe() {
      try {
        const privacy = scope.Shopify && scope.Shopify.customerPrivacy;
        if (!privacy || typeof privacy.currentVisitorConsent !== 'function') return;
        const choice = privacy.currentVisitorConsent();
        if (!choice || typeof choice !== 'object') return;
        // An available API with undecided/no choice is NOT a persisted-only chance.
        if (choice.analytics !== 'yes') block('blocked-known-not-explicit');
      } catch (_) { /* A missing/unready SDK proves nothing; wait for a current read. */ }
    }
    function current() {
      probe();
      if (now() - started > 15000) block('expired-initial-window');
      const url = new URL(scope.location.href);
      const currentLP = LANDINGS.has(url.searchParams.get('lp')) ? url.searchParams.get('lp') : 'classic-six';
      if (url.pathname !== initial.pathname || currentLP !== lp || url.searchParams.has('bereich') || url.searchParams.has('produkt')) block('blocked-not-initial-ring-route');
      return !blocked;
    }
    function decision() { block('blocked-new-consent-decision'); }
    function interaction(event) {
      // Abort before a filter/reset/chip/open action can turn late readiness into
      // a filter-impression or a historical view. No search value is inspected.
      if (event.target && typeof event.target.closest === 'function' && event.target.closest('[data-lws-release]')) block('blocked-storefront-interaction');
    }
    function pagehide() { block('blocked-pagehide'); }
    scope.document.addEventListener('visitorConsentCollected', decision, true);
    for (const name of ['pointerdown', 'click', 'input', 'change']) scope.document.addEventListener(name, interaction, true);
    scope.addEventListener('pagehide', pagehide, true);
    probe(); current();
    return { state, current,
      stop() {
        scope.document.removeEventListener('visitorConsentCollected', decision, true);
        for (const name of ['pointerdown', 'click', 'input', 'change']) scope.document.removeEventListener(name, interaction, true);
        scope.removeEventListener('pagehide', pagehide, true);
      }
    };
  }

  function cents(value) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
    const n = Math.round(value * 100);
    return Number.isSafeInteger(n) && Math.abs(value * 100 - n) < 0.00001 ? n : null;
  }
  function integer(value) { return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0; }
  function nativeId(value) {
    return (typeof value === 'string' || Number.isSafeInteger(value)) && ID.test(String(value)) ? String(value) : null;
  }
  function safeText(value) {
    // Only optional native merchant metadata; never free-form customer fields.
    return typeof value === 'string' && value.length <= 160 && !/[@\r\n<>]|https?:|\b(?:token|email|e-mail)\b/i.test(value) ? value : undefined;
  }
  function displayCents(text) {
    const match = String(text || '').trim().match(/^(?:ab\s+)?((?:\d{1,3}(?:\.\d{3})+|\d+)(?:,\d{1,2})?)\s*€$/);
    if (!match) return null;
    return cents(Number(match[1].replace(/\./g, '').replace(',', '.')));
  }
  function plain(value) {
    return value && Object.prototype.toString.call(value) === '[object Object]' &&
      (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
  }
  function nativeItem(product, variant, expectedCents) {
    const pid = nativeId(product && product.id), vid = nativeId(variant && variant.id);
    if (!pid || !vid || !integer(variant.price) || variant.price !== expectedCents) throw Error('native-price-or-id');
    const item = { item_id: 'shopify_' + COUNTRY + '_' + pid + '_' + vid, price: variant.price / 100, quantity: 1 };
    for (const [key, value] of [['item_name', product.title], ['item_brand', product.vendor], ['item_category', product.type], ['item_variant', variant.public_title || variant.title]]) {
      const text = safeText(value); if (text) item[key] = text;
    }
    return item;
  }
  function indexManifest(manifest) {
    if (!plain(manifest) || manifest.schema !== 1 || !Array.isArray(manifest.products) || manifest.products.length > 2000 ||
        !Array.isArray(manifest.importVerifiedHandles) || manifest.importVerifiedHandles.length > 2000) throw Error('manifest-shape');
    const verified = new Set(manifest.importVerifiedHandles.filter(h => typeof h === 'string' && HANDLE.test(h)));
    const bySku = new Map(); let rows = 0;
    for (const p of manifest.products) {
      if (!plain(p) || !verified.has(p.handle)) continue;
      const skus = Array.isArray(p.skus) ? p.skus : Array.isArray(p.expected) ? p.expected.map(v => v && v.sku) : [];
      rows += skus.length; if (rows > 300000) throw Error('manifest-size');
      for (const sku of skus) {
        if (typeof sku !== 'string' || !SKU.test(sku)) continue;
        if (bySku.has(sku) && bySku.get(sku) !== p.handle) bySku.set(sku, null);
        else if (!bySku.has(sku)) bySku.set(sku, p.handle);
      }
    }
    return bySku;
  }
  function cartItems(cart) {
    if (!plain(cart) || cart.currency !== 'EUR' || !integer(cart.total_price) || !Array.isArray(cart.items) || cart.items.length > 100) throw Error('cart-shape');
    const items = []; let subtotal = 0;
    for (const row of cart.items) {
      if (!plain(row) || !nativeId(row.product_id) || !nativeId(row.variant_id) ||
          !integer(row.final_price) || !Number.isSafeInteger(row.quantity) || row.quantity < 1 || row.quantity > 1000 ||
          (row.id != null && String(row.id) !== String(row.variant_id))) throw Error('cart-item');
      const line = row.final_price * row.quantity;
      if (!Number.isSafeInteger(line) || (row.final_line_price != null && row.final_line_price !== line)) throw Error('cart-price');
      subtotal += line;
      // Allowlist from fresh Ajax cart. No token/key/attributes/properties/address/contact fields.
      const item = { item_id: 'shopify_' + COUNTRY + '_' + row.product_id + '_' + row.variant_id,
        price: row.final_price / 100, quantity: row.quantity };
      items.push(item);
    }
    if (!Number.isSafeInteger(subtotal) || subtotal !== cart.total_price) throw Error('cart-total');
    return items;
  }
  function bootstrap(scope) {
    if (scope[INSTALL_KEY]) return scope[INSTALL_KEY];
    if (!scope.document || !scope.location || !HOSTS.has(scope.location.hostname) || scope.location.protocol !== 'https:') return install(scope);
    const guard = initialListGuard(scope, Date.now);
    let active = install(scope, { initialGuard: guard }), timer = null, attempts = 0, stopped = false;
    if (active.installed) return active;
    if (active.reason !== 'no-existing-data-layer') { guard.stop(); return active; }
    // Google's app may create its queue asynchronously. Wait briefly for THAT
    // array; never create a replacement array, replay its backlog, or load a tag.
    const handle = { installed: false, version: '1.2.0', reason: 'awaiting-existing-data-layer',
      idle: async () => { if (active.installed) await active.idle(); },
      health: () => active.installed ? active.health() : { queueHook: 'awaiting-existing-data-layer', queuedGa4Views: 0 },
      stop: () => { stopped = true; if (timer !== null) scope.clearTimeout(timer);
        scope.document.removeEventListener('visitorConsentCollected', restart, true);
        scope.removeEventListener('pagehide', pause, true);
        scope.removeEventListener('pageshow', restart, true);
        if (active.installed) active.stop(); else guard.stop(); }
    };
    function check() {
      timer = null; if (stopped) return;
      active = install(scope, { initialGuard: guard });
      if (active.installed) {
        handle.installed = true; delete handle.reason;
        scope.document.removeEventListener('visitorConsentCollected', restart, true);
      } else if (active.reason === 'no-existing-data-layer' && ++attempts < 150) timer = scope.setTimeout(check, 100);
      else handle.reason = 'no-existing-data-layer';
    }
    function restart() {
      if (stopped || active.installed) return;
      if (timer !== null) scope.clearTimeout(timer);
      attempts = 0; check(); // Fresh consent starts a new bounded 15-second watch.
    }
    function pause() { if (timer !== null) scope.clearTimeout(timer); timer = null; }
    scope.document.addEventListener('visitorConsentCollected', restart, true);
    scope.addEventListener('pagehide', pause, true);
    scope.addEventListener('pageshow', restart, true);
    restart(); return handle;
  }
  function install(scope, options) {
    options = options || {};
    if (scope[INSTALL_KEY]) return scope[INSTALL_KEY];
    const doc = scope.document, now = options.now || Date.now;
    const location = scope.location;
    if (!doc || !location || !HOSTS.has(location.hostname) || location.protocol !== 'https:') return { installed: false, reason: 'outside-storefront' };
    // A missing Google-owned queue is not a transport. Do not create or replace it.
    const layer = scope.dataLayer;
    if (!Array.isArray(layer) || typeof layer.push !== 'function') return { installed: false, reason: 'no-existing-data-layer' };
    const originalPush = layer.push;
    let nativePageViewEnqueued = false;
    // Passive observation of already-enqueued native metadata, NOT event replay.
    // Scan from the beginning, bounded; future pushes remain eligible after this.
    for (let i = 0; i < Math.min(layer.length, MAX_NATIVE_METADATA_SCAN); i++) {
      if (isNativePageView(layer[i])) { nativePageViewEnqueued = true; break; }
    }
    const guard = options.initialGuard || initialListGuard(scope, now);
    let initialTimer = null;
    let stopped = false, epoch = 0, productGeneration = 0, listGeneration = 0, cartGeneration = 0;
    let productCycle = 0, cartCycle = 0, productFingerprint = null;
    let observedLwsViews = 0, queuedGa4Views = 0;
    const controllers = new Set(), work = new Set(), seen = new Set();
    const nativeCache = new Map(), nativeInflight = new Map(), manifestCache = new Map(), manifestInflight = new Map();
    const q = s => doc.querySelector(s);
    function consent() {
      try { const privacy = scope.Shopify && scope.Shopify.customerPrivacy;
        return privacy && privacy.currentVisitorConsent().analytics === 'yes' && privacy.analyticsProcessingAllowed() === true;
      } catch (_) { return false; }
    }
    function ready() { return !stopped && scope.dataLayer === layer && typeof scope.gtag === 'function' && consent(); }
    function configuration() {
      const node = q('[data-lr-config-json]');
      if (!node || node.textContent.length > 16000) throw Error('config-missing');
      const config = JSON.parse(node.textContent);
      if (!plain(config) || config.moneyCurrency !== 'EUR' || typeof config.root !== 'string' || !/^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?$/.test(config.root)) throw Error('config-currency-or-root');
      const url = new URL(config.nativeHandleIndexUrl, location.href);
      const exactCdn = url.hostname === 'cdn.shopify.com' && url.pathname === CDN_PATH + 'lws-native-handle-index-v3.json';
      const exactOwnSlot = HOSTS.has(url.hostname) && url.pathname === '/cdn/shop/files/lws-native-handle-index-v3.json';
      if (url.protocol !== 'https:' || url.username || url.password || url.hash || (!exactCdn && !exactOwnSlot) ||
          [...url.searchParams.keys()].some(k => k !== 'v') || (url.searchParams.has('v') && !/^\d{1,22}$/.test(url.searchParams.get('v')))) throw Error('manifest-url');
      // Same CONDITIONAL normalization as pinned core. Own-slot current v stays.
      if (exactCdn) url.searchParams.set('v', '1790714154');
      return { root: config.root, index: url.href, internal: config.internal === true };
    }
    function productSnapshot() {
      const dialog = q('[data-lr-product]'), facts = q('[data-lr-facts]'), price = q('[data-lr-detail-price]');
      if (!dialog || !dialog.open || !facts || !price) return null;
      // Span boundaries matter: facts.textContent concatenates SKU and delivery text.
      const matches = Array.from(facts.querySelectorAll('span')).map(node => String(node.textContent).match(/^Artikelreferenz:\s*([A-Za-z0-9-]+)$/)).filter(Boolean);
      if (matches.length !== 1) return null;
      const sku = matches[0][1];
      const amount = displayCents(price.textContent);
      return SKU.test(sku) && amount !== null ? { sku, cents: amount, dialog } : null;
    }
    function visible(node) {
      if (!node || !node.isConnected || node.hidden || node.closest('[hidden], [aria-hidden="true"]')) return false;
      const style = scope.getComputedStyle(node);
      return style.display !== 'none' && style.visibility !== 'hidden' && node.getClientRects().length > 0;
    }
    function listSnapshot() {
      const grid = q('[data-lr-grid]'); if (!grid || !visible(grid)) return [];
      const result = [], skus = new Set();
      for (const link of grid.querySelectorAll('.lr-card-hit[href]')) {
        if (result.length >= MAX_LIST) break;
        if (!visible(link)) continue;
        const url = new URL(link.href, location.href), sku = url.searchParams.get('ausfuehrung');
        const price = link.querySelector('.lr-price'), amount = displayCents(price && price.textContent);
        // Never pass the URL (which may contain UTMs or search text) to Google.
        if (url.origin !== location.origin || !sku || !SKU.test(sku) || skus.has(sku) || amount === null) continue;
        skus.add(sku); result.push({ sku, cents: amount, node: link });
      }
      return result;
    }
    function sameList(snapshot) {
      const current = listSnapshot();
      return snapshot.length === current.length && snapshot.every((v, i) => v.node === current[i].node && v.sku === current[i].sku && v.cents === current[i].cents);
    }
    async function read(url, requestEpoch, limit, cacheMode) {
      if (!ready() || requestEpoch !== epoch) throw Error('obsolete');
      const controller = new scope.AbortController(); controllers.add(controller);
      const timer = scope.setTimeout(() => controller.abort(), 6000);
      try {
        const target = new URL(url, location.href);
        const response = await scope.fetch(target.href, { method: 'GET', cache: cacheMode || 'no-store',
          credentials: target.origin === location.origin ? 'same-origin' : 'omit', signal: controller.signal });
        if (!response.ok) throw Error('lookup-failed');
        const text = await response.text(); if (text.length > limit) throw Error('response-size');
        if (!ready() || requestEpoch !== epoch) throw Error('obsolete');
        return JSON.parse(text);
      } finally { scope.clearTimeout(timer); controllers.delete(controller); }
    }
    async function manifest(url, requestEpoch) {
      const cached = manifestCache.get(url);
      if (cached && now() - cached.time < INDEX_TTL_MS) return cached.index;
      const key = requestEpoch + ':' + url;
      let lookup = manifestInflight.get(key);
      if (!lookup) {
        // Identity manifests may reuse the exact versioned CDN response already
        // fetched by Core. Native product prices and cart remain no-store.
        lookup = read(url, requestEpoch, 12000000, 'default').then(value => {
          const index = indexManifest(value); if (requestEpoch !== epoch) throw Error('obsolete');
          manifestCache.set(url, { index, time: now() }); return index;
        });
        manifestInflight.set(key, lookup);
        lookup.finally(() => manifestInflight.delete(key)).catch(() => {});
      }
      return lookup;
    }
    async function resolve(sku, price, config, requestEpoch) {
      const primary = await manifest(config.index, requestEpoch);
      let handle = primary.get(sku);
      // A duplicate/ambiguous primary SKU must NOT be repaired by another manifest.
      if (!primary.has(sku) && config.internal) handle = (await manifest(MEMOIRE_INDEX, requestEpoch)).get(sku);
      if (!handle || !HANDLE.test(handle)) throw Error('native-handle-missing');
      let product, cached = nativeCache.get(handle);
      if (cached && now() - cached.time < PRICE_TTL_MS) product = cached.product;
      else {
        const key = requestEpoch + ':' + handle;
        let lookup = nativeInflight.get(key);
        if (!lookup) {
          lookup = read(new URL(config.root + 'products/' + encodeURIComponent(handle) + '.js', location.origin).href, requestEpoch, 5000000)
            .then(value => { if (requestEpoch === epoch && ready()) {
              nativeCache.set(handle, { product: value, time: now() });
              if (nativeCache.size > 60) nativeCache.delete(nativeCache.keys().next().value);
            } return value; });
          nativeInflight.set(key, lookup);
          lookup.finally(() => nativeInflight.delete(key)).catch(() => {});
        }
        product = await lookup;
      }
      if (!plain(product) || product.handle !== handle || !Array.isArray(product.variants) || product.variants.length > 2048) throw Error('native-product-shape');
      const variants = product.variants.filter(v => plain(v) && v.sku === sku);
      if (variants.length !== 1) throw Error('native-variant-ambiguous');
      return nativeItem(product, variants[0], price);
    }
    function remember(key) {
      seen.add(key); if (seen.size > 250) seen.delete(seen.values().next().value);
    }
    function send(name, payload, key, isCurrent) {
      if (!ready() || !isCurrent() || seen.has(key)) return;
      // Explicit route, exact allowlisted payload, no default-pageview/config writes.
      const pathname = location.pathname;
      if (typeof pathname !== 'string' || !/^\/[a-zA-Z0-9/_-]*$/.test(pathname)) return;
      const parameters = Object.assign({ send_to: MEASUREMENT_ID, currency: 'EUR',
        page_location: location.origin + pathname, page_referrer: '', page_title: 'Liebreiz Shop' }, payload);
      scope.gtag('event', name, parameters);
      queuedGa4Views++;
      remember(key);
    }
    async function observe(event, initialDOM) {
      if (!plain(event) || !['view_item', 'select_variant', 'view_item_list', 'view_cart'].includes(event.event) ||
          event.ecommerce != null || event.currency !== 'EUR' || !LANDINGS.has(event.landing_page)) return;
      observedLwsViews++;
      // A new selection invalidates old asynchronous work even when consent/gtag is absent.
      const kind = event.event === 'select_variant' ? 'view_item' : event.event;
      const generation = kind === 'view_item' ? ++productGeneration : kind === 'view_cart' ? ++cartGeneration : ++listGeneration;
      // Also drop a NEW early Core list; never let it beat the native session
      // starter. No replay; only the bounded NEW DOM observation may follow.
      if (kind === 'view_item_list' && !nativePageViewEnqueued) return;
      if (!ready()) return; // No pre-consent queue, no backlog replay, no gtag installation.
      const requestEpoch = epoch, started = now();
      const baseCurrent = () => requestEpoch === epoch && now() - started <= VIEW_DEADLINE_MS && ready() &&
        (!initialDOM || (nativePageViewEnqueued && guard.current()));
      const config = configuration();
      if (kind === 'view_item') {
        if (typeof event.item_id !== 'string' || !SKU.test(event.item_id) || Array.isArray(event.items)) return;
        const amount = cents(event.value), snapshot = productSnapshot();
        if (amount === null || !snapshot || snapshot.sku !== event.item_id || snapshot.cents !== amount) return;
        const fingerprint = event.item_id + ':' + amount;
        if (fingerprint !== productFingerprint) { productCycle++; productFingerprint = fingerprint; }
        const key = requestEpoch + ':item:' + productCycle + ':' + fingerprint;
        const isCurrent = () => { const current = productSnapshot(); return baseCurrent() && generation === productGeneration && current && current.dialog === snapshot.dialog && current.sku === snapshot.sku && current.cents === snapshot.cents; };
        if (seen.has(key)) return;
        const item = await resolve(snapshot.sku, amount, config, requestEpoch);
        // select_variant intentionally means the newly displayed native variant is viewed.
        send('view_item', { value: amount / 100, items: [item] }, key, isCurrent);
      } else if (kind === 'view_item_list') {
        if (!Array.isArray(event.items) || event.item_list_id !== event.landing_page) return;
        const snapshot = listSnapshot(); if (!snapshot.length) return;
        const key = requestEpoch + ':list:' + event.landing_page + ':' + snapshot.map(v => v.sku + ':' + v.cents).join('|');
        if (seen.has(key)) return;
        const isCurrent = () => baseCurrent() && generation === listGeneration && sameList(snapshot);
        const items = new Array(snapshot.length); let cursor = 0;
        await Promise.all(Array.from({ length: Math.min(4, snapshot.length) }, async () => {
          while (cursor < snapshot.length) {
            if (!isCurrent()) throw Error('obsolete');
            const i = cursor++, row = snapshot[i];
            items[i] = Object.assign(await resolve(row.sku, row.cents, config, requestEpoch), { index: i, item_list_id: event.landing_page });
          }
        }));
        send('view_item_list', { item_list_id: event.landing_page, items }, key, isCurrent);
      } else {
        const dialog = q('[data-lr-cart]'), total = q('[data-lr-cart-total]'), container = q('[data-lr-cart-items]'), amount = cents(event.value);
        if (!dialog || !dialog.open || !total || !container || amount === null || displayCents(total.textContent) !== amount || event.items != null) return;
        const rows = Array.from(container.querySelectorAll('.lr-cart-item'));
        if (!rows.length) return;
        const isCurrent = () => {
          const currentRows = Array.from(container.querySelectorAll('.lr-cart-item'));
          return baseCurrent() && generation === cartGeneration && dialog.open && q('[data-lr-cart]') === dialog &&
            q('[data-lr-cart-items]') === container && displayCents(total.textContent) === amount &&
            rows.length === currentRows.length && rows.every((row, i) => row === currentRows[i]);
        };
        const cart = await read(new URL(config.root + 'cart.js', location.origin).href, requestEpoch, 2000000);
        const items = cartItems(cart);
        if (!items.length || items.length !== rows.length || cart.total_price !== amount) return;
        const key = requestEpoch + ':cart:' + cartCycle + ':' + amount + ':' + items.map(v => [v.item_id, v.price, v.quantity].join(':')).join('|');
        send('view_cart', { value: amount / 100, items }, key, isCurrent);
      }
    }
    function push() {
      const args = Array.from(arguments);
      const result = originalPush.apply(this, args); // Preserve Google's/native queue semantics.
      // Mark only AFTER original queue processing; enqueue order, not a collector
      // certificate. No own page_view, base tag, session or campaign-data copying.
      if (this === layer && !stopped && !nativePageViewEnqueued) {
        for (const entry of args) if (isNativePageView(entry)) { nativePageViewEnqueued = true; break; }
      }
      if (this === layer && !stopped) for (const entry of args) {
        // The async function captures consent/epoch/generation NOW, before its first
        // await: a later grant must not rescue a pre-consent entry in a microtask.
        const job = observe(entry).catch(() => {});
        work.add(job); job.finally(() => work.delete(job));
      }
      return result;
    }
    function invalidate() {
      epoch++; productGeneration++; listGeneration++; cartGeneration++;
      nativeCache.clear(); nativeInflight.clear(); manifestCache.clear(); manifestInflight.clear(); seen.clear();
      productCycle++; cartCycle++; productFingerprint = null;
      for (const controller of controllers) controller.abort();
    }
    function close(event) {
      if (event.target === q('[data-lr-product]')) { productGeneration++; productCycle++; productFingerprint = null; }
      if (event.target === q('[data-lr-cart]')) { cartGeneration++; cartCycle++; }
    }
    layer.push = push;
    doc.addEventListener('visitorConsentCollected', invalidate, true);
    doc.addEventListener('close', close, true);
    scope.addEventListener('pagehide', invalidate, true);
    function initialDOMList() {
      initialTimer = null;
      if (stopped || guard.state.attempted || !guard.current()) return;
      const root = q('[data-lws-release]');
      const product = q('[data-lr-product]'), cart = q('[data-lr-cart]');
      if (!nativePageViewEnqueued || !ready() || !root || root.dataset.ready !== 'true' || !listSnapshot().length || (product && product.open) || (cart && cart.open)) {
        initialTimer = scope.setTimeout(initialDOMList, 100); return;
      }
      // A NEW current DOM observation, never an old dataLayer entry. The same
      // list observer/dedupe/IDs/prices is used without republishing a raw event.
      guard.state.attempted = true; guard.state.status = 'captured-current-dom-once';
      const job = observe({ event: 'view_item_list', currency: 'EUR', landing_page: guard.state.landing,
        item_list_id: guard.state.landing, items: [] }, true).catch(() => { guard.state.status = 'failed-initial-lookup-or-validation'; });
      work.add(job); job.finally(() => work.delete(job));
    }
    if (options.initialDomList !== false) initialTimer = scope.setTimeout(initialDOMList, 0);
    const adapter = { installed: true, version: '1.2.0',
      // Useful only for local QA; no payloads, consent details or network bodies logged.
      idle: async () => { while (work.size) await Promise.all(Array.from(work)); },
      // Push may be changed to a chaining wrapper or replaced outright. Without
      // mutating/sending probes we cannot certify that chain: mark it for live QA.
      health: () => ({ queueHook: stopped ? 'stopped' : scope.dataLayer !== layer ? 'queue-replaced' : layer.push === push ? 'direct' : 'push-changed-needs-qa',
        nativeSenderPresent: typeof scope.gtag === 'function', observedLwsViews, queuedGa4Views,
        nativePageViewEnqueued,
        initialDomListStatus: guard.state.status, initialDomListAttempted: guard.state.attempted }),
      stop: () => { if (stopped) return; stopped = true; invalidate(); if (layer.push === push) layer.push = originalPush;
        if (initialTimer !== null) scope.clearTimeout(initialTimer); guard.stop();
        doc.removeEventListener('visitorConsentCollected', invalidate, true); doc.removeEventListener('close', close, true);
        scope.removeEventListener('pagehide', invalidate, true); }
    };
    scope[INSTALL_KEY] = adapter; return adapter;
  }
  return { bootstrap, install, cents, displayCents, nativeItem, indexManifest, cartItems, isNativePageView };
});
